// FLAG:docuAiEnhancements — one turn, at most three automatic retries.
export const MAX_CHAT_RETRIES = 3
const RETRY_DELAYS = [1000, 2000, 4000]

export interface ChatStreamEvent {
  type?: string
  data?: { sources?: Array<{ source?: string }>, reply?: string, token?: string }
}

class ChatHttpError extends Error {
  constructor(readonly status: number) { super('rag chat: ' + status) }
}

const checkAborted = (signal: AbortSignal) => {
  if (signal.aborted) throw new DOMException('Chat cancelled', 'AbortError')
}

const waitForRetry = (delay: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  checkAborted(signal)
  const onAbort = () => {
    clearTimeout(timer)
    reject(new DOMException('Chat cancelled', 'AbortError'))
  }
  const timer = setTimeout(() => {
    signal.removeEventListener('abort', onAbort)
    resolve()
  }, delay)
  signal.addEventListener('abort', onAbort, { once: true })
})

const readAnswer = async(response: Response, signal: AbortSignal, onEvent: (event: ChatStreamEvent) => void) => {
  if (!response.ok) {
    response.body?.cancel().catch(() => {})
    throw new ChatHttpError(response.status)
  }
  if (!response.body) throw new Error('rag chat: missing stream')
  const reader = response.body.getReader()
  const onAbort = () => { reader.cancel().catch(() => {}) }
  signal.addEventListener('abort', onAbort, { once: true })
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    for (;;) {
      checkAborted(signal)
      const { done, value } = await reader.read()
      checkAborted(signal)
      if (done) throw new Error('rag chat: incomplete stream')
      buffer += decoder.decode(value, { stream: true })
      let separator = /\r?\n\r?\n/.exec(buffer)
      while (separator) {
        const raw = buffer.slice(0, separator.index).trim()
        buffer = buffer.slice(separator.index + separator[0].length)
        separator = /\r?\n\r?\n/.exec(buffer)
        if (!raw.startsWith('data:')) continue
        const payload = raw.slice(5).trim()
        if (!payload || payload === '[DONE]') continue
        let event: ChatStreamEvent
        try { event = JSON.parse(payload) } catch { continue }
        if (!event || typeof event !== 'object') continue
        if (event.type === 'error') throw new Error('rag chat: upstream error')
        onEvent(event)
        // A completed answer must never be repeated because closing the stream fails later.
        if (event.type === 'done') return
      }
    }
  } finally {
    signal.removeEventListener('abort', onAbort)
    reader.cancel().catch(() => {})
  }
}

/** The caller captures one immutable request body; failed partial answers never enter history. */
export const streamChatWithRetries = async({ request, signal, onEvent, onRetry }: {
  request: () => Promise<Response>
  signal: AbortSignal
  onEvent: (event: ChatStreamEvent) => void
  onRetry: (retry: number) => void
}) => {
  for (let retry = 0; ; retry += 1) {
    checkAborted(signal)
    try {
      await readAnswer(await request(), signal, onEvent)
      return
    } catch (error) {
      checkAborted(signal)
      const permanent = error instanceof ChatHttpError
        && error.status < 500 && error.status !== 408 && error.status !== 429
      if (permanent || retry >= MAX_CHAT_RETRIES) throw error
      onRetry(retry + 1)
      await waitForRetry(RETRY_DELAYS[retry], signal)
    }
  }
}
